import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { TemplatedReportsCategoriesDetailRoutePath } from "#src/routes/reports/templated-reports/categories/[categoryId]";
import { TemplatedReportsTemplateDetailRoutePath } from "#src/routes/reports/templated-reports/template/[templateName]";
import { useQuery } from "@tanstack/react-query";
import {
  Accordion,
  KeyValueList,
  KeyValuePairList,
  Skeleton,
} from "@validereinc/common-components";
import {
  ReportV3Adapter,
  Resources,
  TemplatedReportAdapter,
  validateWorkflowVariableWithRegex,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React from "react";
import styles from "./WorkflowActionAssessTemplatedReportForComments.module.scss";

const cx = classNames.bind(styles);

const { AccordionPanel } = Accordion;

export const WorkflowActionAssessTemplatedReportForComments = ({
  reportId,
}: {
  reportId: string;
}) => {
  const isReportIdWorkflowVariable =
    validateWorkflowVariableWithRegex(reportId);
  const { data: reportData, isLoading: isLoadingReport } = useQuery({
    queryKey: [Resources.REPORT, reportId],
    queryFn: () => {
      if (!reportId) {
        return;
      }

      return ReportV3Adapter.getOne({ id: reportId });
    },
    enabled: Boolean(reportId) && !isReportIdWorkflowVariable,
  });
  const report = reportData?.data;
  const templatedReportName = report?.templated_report_name;

  const { data: templatedReportData, isLoading: isTemplatedReportLoading } =
    useQuery({
      queryKey: [Resources.TEMPLATED_REPORT, templatedReportName],
      queryFn: () => {
        if (!templatedReportName) {
          return;
        }

        return TemplatedReportAdapter.getOne({ id: templatedReportName });
      },
      enabled: Boolean(templatedReportName),
    });
  const templatedReport = templatedReportData?.data;
  const templatedReportCategoryId = templatedReport?.category?.id;
  const templatedReportCategoryName = templatedReport?.category?.name;

  const data: KeyValuePairList = [
    {
      title: "Template",
      value: templatedReportName ? (
        <RoutingLink
          to={TemplatedReportsTemplateDetailRoutePath.toLinkParts({
            pathParams: { templateName: templatedReportName },
          })}
        >
          {templatedReportName}
        </RoutingLink>
      ) : (
        "-"
      ),
    },
    {
      title: "Category",
      value: templatedReportCategoryName ? (
        <RoutingLink
          to={TemplatedReportsCategoriesDetailRoutePath.toLinkParts({
            pathParams: {
              categoryId: templatedReportCategoryId,
            },
          })}
        >
          {templatedReportCategoryName}
        </RoutingLink>
      ) : (
        "-"
      ),
    },
  ];

  const isLoading =
    (Boolean(reportId) && !isReportIdWorkflowVariable && isLoadingReport) ||
    (Boolean(templatedReportName) && isTemplatedReportLoading);

  return (
    <Accordion defaultActiveKeys={["report"]}>
      <AccordionPanel
        dataKey="report"
        title="Report Overview"
      >
        <Skeleton
          isLoading={isLoading}
          style={{ minHeight: 200 }}
        >
          <KeyValueList
            className={cx("keyValueListContainer")}
            data={data}
          />
        </Skeleton>
      </AccordionPanel>
    </Accordion>
  );
};
